import { useState, useEffect } from 'react';
import {
  DeleteDialog,
  ExcelImportDialog,
  ExcelImportErrorDialog,
  ErpDataTableV2,
  BulkDeleteDialogV4
} from '@/components/custom/erp';
import { useCRUD, useExcelProcessor, useForms } from '@/hooks';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { DonViTinh, DonViTinhInput } from '@/types/schemas';
import { getDataTableColumns } from './cols-definition';
import { ActionBar, UnitDialog } from './components';
import { useAuth } from '@/contexts/auth-context';
import { ExcelConfig, ImportBody } from './utils';
import { toast } from '@/hooks/use-toast';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';
import api from '@/lib/api';

const PAGE_SIZE = 20;

export default function UOMPageContent() {
  const { entityUnit, entity } = useAuth();
  const { processFile } = useExcelProcessor(ExcelConfig);

  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);

  const {
    data,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    searchData
  } = useCRUD<DonViTinh, DonViTinhInput>({
    endpoint: QUERY_KEYS.DON_VI_TINH,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode: mode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    isImportDialogOpen,
    setIsImportDialogOpen,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection,
    handleOpenImportExcel
  } = useForms<DonViTinh>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : data || [];

  useEffect(() => {
    const handleGlobalShortcut = (event: KeyboardEvent) => {
      if (!event.ctrlKey || event.key.toLowerCase() !== 'd') {
        return;
      }

      if (showForm || showDelete || isBulkDeleteOpen || displayData.length === 0) {
        return;
      }

      event.preventDefault();
      setIsBulkDeleteOpen(true);
    };

    window.addEventListener('keydown', handleGlobalShortcut);
    return () => window.removeEventListener('keydown', handleGlobalShortcut);
  }, [displayData.length, isBulkDeleteOpen, showDelete, showForm]);
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: getDataTableColumns(handleViewClick)
    }
  ];

  const handleSubmit = async (data: DonViTinhInput) => {
    try {
      if (mode === 'add') {
        await addItem(data);
      } else if (mode === 'edit' && selectedObj) {
        await updateItem(selectedObj.uuid, data);
      }
      handleCloseForm();
      clearSelection();
      await refreshData();
    } catch (error) {
      return;
    }
  };

  const handleImportBodies = async (bodies: ImportBody[]) => {
    try {
      for (const body of bodies) {
        await addItem({
          dvt: body.dvt,
          ten_dvt: body.ten_dvt,
          ten_khac: body.ten_khac,
          dvt2: body.dvt2,
          status: body.status
        });
      }
      await refreshData();
      toast({
        description: 'Đã nhập danh mục đơn vị tính từ Excel thành công',
        variant: 'default'
      });
    } catch (error) {
      console.error('Error importing units from Excel:', error);
      toast({
        description: 'Có lỗi xảy ra khi nhập dữ liệu từ Excel',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!entityUnit?.uuid) {
      toast({
        description: 'Không tìm thấy thông tin đơn vị',
        variant: 'destructive'
      });
      return;
    }

    if (!data.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      return;
    }

    const maxErrors = parseInt(data.errors || '200', 10);
    const sheetIndex = parseInt(data.sheetIndex || '0', 10);

    const result = await processFile({
      file: data.file,
      sheetIndex,
      maxErrors,
      context: { entityUnitUUID: entityUnit.uuid }
    });

    if (result.success && result.data) {
      if (result.data.length === 0) {
        toast({
          description:
            'Không tìm thấy dữ liệu hợp lệ. Vui lòng kiểm tra header Excel có khớp với mẫu: ' +
            Object.values(ExcelConfig.Headers).join(', '),
          variant: 'destructive'
        });
        return;
      }
      await handleImportBodies(result.data);
      setIsImportDialogOpen(false);
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
      setIsImportDialogOpen(false);
    }
  };

  const handleExportExcel = async () => {
    if (!entity?.slug) {
      toast({
        description: 'Không tìm thấy thông tin đơn vị',
        variant: 'destructive'
      });
      return;
    }

    try {
      const XLSX = await import('xlsx');
      const url = `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_VI_TINH}/`;
      const response = await api.get(url, {
        params: {
          page: 1,
          page_size: 100000
        }
      });
      const allData = (response.data.results || response.data || []) as DonViTinh[];

      const exportData = allData.map(item => {
        const status = item.status as unknown;
        return {
          'Đơn vị tính': item.dvt || '',
          'Diễn giải': item.ten_dvt || '',
          'Tên khác': item.ten_khac || '',
          'Mã quy đổi': item.dvt2 || '',
          'Trạng thái': status === '1' || status === 1 || status === true ? 'Đang hoạt động' : 'Ngừng hoạt động'
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'UOM');
      XLSX.writeFile(workbook, 'Danh mục đơn vị tính.xlsx');

      toast({
        description: 'Kết xuất dữ liệu Excel thành công',
        variant: 'default'
      });
    } catch (error) {
      console.error('Error exporting data:', error);
      toast({
        description: 'Có lỗi xảy ra khi kết xuất dữ liệu',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
      <ActionBar
        onAddIconClick={handleAddClick}
        onEditIconClick={() => selectedObj && handleEditClick()}
        onDeleteIconClick={() => selectedObj && handleDeleteClick()}
        onCopyIconClick={() => selectedObj && handleCopyClick()}
        onWatchIconClick={() => selectedObj && handleViewClick()}
        onRefreshClick={refreshData}
        isEditDisabled={!selectedObj}
        isViewDisabled={!selectedObj}
        isDeleteDisabled={!selectedObj}
        isCopyDisabled={!selectedObj}
        onImportExcel={handleOpenImportExcel}
        onExportExcel={handleExportExcel}
      />

      <div data-excel-operation-overlay-root className='min-h-0 flex-1 overflow-hidden'>
        <ErpDataTableV2
          tables={tables}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowIndex || undefined}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          serverSidePagination={true}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
        />
      </div>

      {showForm && (
        <UnitDialog
          mode={mode}
          open={showForm}
          onClose={handleCloseForm}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onCopyButtonClick={handleCopyClick}
          onWatchButtonClick={handleViewClick}
          onDeleteButtonClick={handleDeleteClick}
          onSubmit={handleSubmit}
          initialData={mode === 'add' && !isCopyMode ? undefined : detail}
        />
      )}

      {showDelete && (
        <DeleteDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          deleteObj={deleteItem}
          clearSelection={clearSelection}
        />
      )}

      {isImportDialogOpen && (
        <ExcelImportDialog
          open={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onSubmit={handleImportFromExcel}
          title='Nhập dữ liệu từ Excel'
        />
      )}

      {showErrorDialog && errorData && (
        <ExcelImportErrorDialog
          open={showErrorDialog}
          onClose={() => {
            setShowErrorDialog(false);
            setErrorData(null);
          }}
          errors={errorData.errors}
          columnMapping={errorData.columnMapping}
        />
      )}

      {isBulkDeleteOpen && (
        <BulkDeleteDialogV4
          title='Đơn vị tính'
          onClose={() => setIsBulkDeleteOpen(false)}
          columns={tables[0].columns}
          dataRows={displayData}
          handleDelete={deleteItem}
          onComplete={() => void refreshData()}
        />
      )}
    </div>
  );
}
